import { Tooltip } from '@mui/material';
import { Button } from '@trackman/web-shared-components';
import { ServiceTypes } from 'constants/ServiceTypes';
import { ISessionMode } from 'interfaces/session/ISessionMode';
import { observer } from 'mobx-react-lite';
import LineupManager from 'models/setLineup/LineupManager';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { IFeatureConfigService } from 'services/FeatureConfigService';
import { useInjection } from 'services/ioc/ioc.react';
import LineupProgress from '../LineupProgress/LineupProgress';
import StrikeZoneMissing from '../StrikeZoneMissing/StrikeZoneMissing';
import { ButtonWrapper, Divider, LeftSide, RightSide, SetLineupBottomBarWrapper } from './SetLineupBottomBarStyles';

interface IProps {
  onClickStartTaggingCallback: () => void;
  onClickSwapTeamsCallback: () => void;
  startTaggingDisabled: boolean;
  isLoading: boolean;
  sessionMode: ISessionMode;
  isBattingPractice?: boolean;
  homeTeamManager?: LineupManager;
  awayTeamManager?: LineupManager;
  requiredPositionsCount?: number;
  homePositions?: string[];
  awayPositions?: string[];
}

const SetLineupBottomBar: React.FC<IProps> = observer(
  ({
    onClickStartTaggingCallback,
    startTaggingDisabled,
    isLoading,
    onClickSwapTeamsCallback,
    sessionMode,
    isBattingPractice,
    homeTeamManager,
    awayTeamManager,
    requiredPositionsCount,
    homePositions,
    awayPositions,
  }) => {
    const featureConfigService = useInjection<IFeatureConfigService>(ServiceTypes.FeatureConfigService);
    const [duplicatedPosition, setDuplicatedPosition] = useState(false);
    const history = useHistory();

    useEffect(() => {
      setDuplicatedPosition(() => {
        let result = false;
        if (homePositions) {
          const positions = homePositions.filter((pos) => pos !== 'DH');
          result = new Set(positions).size < positions.length;
        }

        if (awayPositions) {
          const positions = awayPositions.filter((pos) => pos !== 'DH');

          result = new Set(positions).size < positions.length;
        }

        return result;
      });
    }, [homePositions, awayPositions]);

    return (
      <SetLineupBottomBarWrapper>
        <LeftSide>
          <Button
            intent='tertiary'
            label='Cancel'
            onClick={() => history.push('/')}
            data-test='lineup-cancel-btn'
            style={{ backgroundColor: '#F5F5F5', color: '#414141' }}
            width={75}
          />
          {featureConfigService.getConfig().isRoboUmpEnabled &&
            [...(homeTeamManager?.playersWithMissingSZ || []), ...(awayTeamManager?.playersWithMissingSZ || [])].length > 0 && (
              <>
                <Divider />
                <StrikeZoneMissing
                  players={[...(homeTeamManager?.playersWithMissingSZ || []), ...(awayTeamManager?.playersWithMissingSZ || [])]}
                />
              </>
            )}
        </LeftSide>
        <RightSide>
          {!isBattingPractice && (
            <LineupProgress
              homeTeamManager={homeTeamManager}
              awayTeamManager={awayTeamManager}
              requiredPositionsCount={requiredPositionsCount}
            />
          )}
          {duplicatedPosition ? (
            <Tooltip
              title={
                <div>
                  <div>Can’t start due to</div>
                  <div>duplicated positions</div>
                </div>
              }
              arrow
              componentsProps={{ tooltip: { className: 'duplicated-position-tooltip' } }}
              placement='bottom'
            >
              <ButtonWrapper>
                <Button
                  intent='primary'
                  label={isBattingPractice ? 'Start BP' : 'Start tagging'}
                  onClick={() => {}}
                  isLoading={isLoading}
                  data-test='start-tagging'
                  width='119px'
                />
              </ButtonWrapper>
            </Tooltip>
          ) : (
            <Button
              intent='primary'
              label={isBattingPractice ? 'Start BP' : 'Start tagging'}
              onClick={onClickStartTaggingCallback}
              isLoading={isLoading}
              disabled={startTaggingDisabled}
              data-test='start-tagging'
              width='119px'
            />
          )}
        </RightSide>
      </SetLineupBottomBarWrapper>
    );
  }
);

SetLineupBottomBar.displayName = 'SetLineupBottomBar';

export default SetLineupBottomBar;
